<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12">
                <v-card 
                    flat 
                    class="my-9 mx-5 pa-9 rounded-xl"
                    role="region"
                    :aria-label="$t('user-management.title')">
                    <v-card-title class="card-title px-0 pt-0">
                        <span role="heading" aria-level="1">
                            {{ formatMenuText($t('user') + ' ' + $t('management')).split(' ')[0] }}&nbsp;<span class="card-title font-weight-medium">{{ formatMenuText($t('user') + ' ' + $t('management')).split(' ')[1] }}</span>
                        </span>
                    </v-card-title>
                    <v-row align="center" justify="space-between">
                        <v-col cols="12" sm="4" class="ma-1 order-last order-sm-first">
                            <v-row align="center">
                                <v-text-field v-model="search"
                                              outlined
                                              dense
                                              clearable
                                              hide-details
                                              maxlength="255"
                                              prepend-inner-icon="mdi-magnify"
                                              ref="searchField"
                                              :label="$t('accessibility.searchUsers')"
                                              :aria-label="$t('accessibility.searchUsers')"
                                              @blur="loadUsers"
                                              @change="loadUsers"
                                              @click:clear="onClearSearch">
                                </v-text-field>
                            </v-row>
                        </v-col>
                        <v-col cols="12" sm="auto" class="ma-1">
                            <v-row align="center" justify="end">
                                <v-hover v-slot="{hover}">
                                    <v-btn :color="hover ? 'buttonHover' : 'primary'"
                                           :disabled="editedUserIndex > -1 || savingNewUser"
                                           :dark="editedUserIndex === -1"
                                           depressed
                                           class="text-none"
                                           @click="createUser"
                                           :aria-label="$t('user-management.addUser')">
                                        <v-icon class="mr-1" aria-hidden="true">mdi-account-plus-outline</v-icon>
                                        {{$t('user-management.addUser')}}
                                    </v-btn>
                                </v-hover>
                                <v-hover v-slot="{hover}">
                                    <v-btn :color="hover ? 'buttonHover' : 'primary'"
                                           depressed
                                           dark
                                           class="text-none ml-2"
                                           @click="openColumnSelector"
                                           :aria-label="$t('user-management.changeCustomerColumns')">
                                        {{$t('user-management.changeCustomerColumns')}}
                                    </v-btn>
                                </v-hover>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-row align="center">
                        <v-col>
                            <span id="role-filters-label">{{$t('user-management.roleFilters')}}: &nbsp;</span>
                            <v-btn-toggle 
                                class="flex-wrap" 
                                v-model="roleFilters" 
                                dense 
                                multiple
                                role="group"
                                aria-labelledby="role-filters-label">
                                <template v-for="(roleFilter, rfi) in roleList">
                                    <v-hover v-slot="{hover}" :key="`roleFilter_${rfi}`">
                                        <v-btn :color="hover ? 'buttonHover' : roleFilters.includes(rfi) ? 'primary' : ''"
                                               :dark="roleFilters.includes(rfi)"
                                               depressed
                                               :class="roleFilters.includes(rfi) ? 'white--text' : ''"
                                               :aria-label="roleFilter.text"
                                               :aria-pressed="roleFilters.includes(rfi) ? 'true' : 'false'">
                                            {{roleFilter.text}}
                                        </v-btn>
                                    </v-hover>
                                </template>
                            </v-btn-toggle>
                        </v-col>
                    </v-row>
                    <reportAccessComponent :userId="editedUser.Id" :dialog="dialogReportAccess" @cancel="onReportAccessCancel" @save="onReportAccessSave"></reportAccessComponent>
                    <v-form v-model="formValid">
                        <v-data-table class="mt-2"
                                      :headers="listHeaders"
                                      :items="users"
                                      :search="search"
                                      item-key="Id"
                                      :items-per-page="tableOptions.itemsPerPage"
                                      :loading="loading"
                                      :options.sync="tableOptions"
                                      :header-props="{ 'sort-icon': 'mdi-triangle-small-up'} "
                                      :footer-props="{
                                    itemsPerPageOptions: [10, 25, 50, 100, 200],
                                    itemsPerPageText: $t('user-management.itemsPerPage'),
                                    showFirstLastPage: true,
                                    disableItemsPerPage: false
                                  }"
                                      single-select
                                      :server-items-length="totalUsers"
                                      :aria-label="$t('accessibility.usersTable')">
                            <template v-slot:item.Id="props">
                                <td :class="isEditing(props.item.Id) ? 'row-backgroundColor' : ''">
                                    {{props.item.Id > 0 ? props.item.Id : ''}}
                                </td>
                            </template>
                            <template v-slot:item.Role_NameFr="props">
                                <td v-if="isEditing(props.item.Id)" class="row-backgroundColor">
                                    <v-select v-model="editedUser.Role_Code"
                                              background-color="white"
                                              outlined dense hide-details
                                              :items="roleList"
                                              :placeholder="$t('role')"
                                              :rules="requiredRules"
                                              :label="$t('role')"
                                              :aria-label="$t('accessibility.selectUserRole')"></v-select>
                                </td>
                                <td v-else>
                                    {{displayUserRoleName(props.item)}}
                                </td>
                            </template>
                            <template v-slot:item.FirstName="props">
                                <td v-if="isEditing(props.item.Id)" class="row-backgroundColor">
                                    <v-text-field v-model="editedUser.FirstName"
                                                  :placeholder="$t('firstname')"
                                                  :label="$t('firstname')"
                                                  background-color="white"
                                                  outlined
                                                  dense
                                                  hide-details
                                                  required
                                                  maxlength="100"
                                                  class="text-small"
                                                  :rules="firstnameRules"
                                                  :aria-label="$t('firstname')"
                                                  :aria-required="true"></v-text-field>
                                </td>
                                <td v-else>
                                    {{ props.item.FirstName }}
                                </td>

                            </template>
                            <template v-slot:item.LastName="props">
                                <td v-if="isEditing(props.item.Id)" class="row-backgroundColor">
                                    <v-text-field v-model="editedUser.LastName"
                                                  :placeholder="$t('lastname')"
                                                  :label="$t('lastname')"
                                                  background-color="white"
                                                  outlined
                                                  dense
                                                  hide-details
                                                  required
                                                  maxlength="100"
                                                  class="text-small"
                                                  :rules="lastnameRules"
                                                  :aria-label="$t('lastname')"
                                                  :aria-required="true"></v-text-field>
                                </td>
                                <td v-else>
                                    {{ props.item.LastName }}
                                </td>
                            </template>
                            <template v-slot:item.Username="props">
                                <td v-if="isEditing(props.item.Id)" class="row-backgroundColor">
                                    <v-text-field v-model="editedUser.Username"
                                                  :placeholder="$t('username')"
                                                  :label="$t('username')"
                                                  background-color="white"
                                                  outlined
                                                  dense
                                                  hide-details
                                                  required
                                                  maxlength="50"
                                                  class="text-small"
                                                  :rules="usernameRules"
                                                  :aria-label="$t('username')"
                                                  :aria-required="true"></v-text-field>
                                </td>
                                <td v-else>
                                    {{ props.item.Username }}
                                </td>
                            </template>
                            <template v-slot:item.Email="props">
                                <td v-if="isEditing(props.item.Id)" class="row-backgroundColor">
                                    <v-text-field v-model="editedUser.Email"
                                                  :placeholder="$t('email')"
                                                  :label="$t('email')"
                                                  background-color="white"
                                                  outlined
                                                  dense
                                                  hide-details
                                                  required
                                                  maxlength="255"
                                                  class="text-small"
                                                  :rules="emailRules"
                                                  :aria-label="$t('email')"
                                                  :aria-required="true"
                                                  type="email"></v-text-field>
                                </td>
                                <td v-else>
                                    {{ props.item.Email }}
                                </td>
                            </template>
                            <template v-slot:item.Client_NameFR="props">
                                <td v-if="isEditing(props.item.Id)" class="row-backgroundColor">
                                    <v-select v-model="editedUser.Client_Code"
                                              background-color="white"
                                              outlined dense hide-details
                                              :items="clientList"
                                              :placeholder="$t('client')"
                                              :rules="requiredRules"
                                              :label="$t('client')"
                                              :aria-label="$t('accessibility.selectClient')"></v-select>
                                </td>
                                <td v-else>
                                    {{displayClientName(props.item)}}
                                </td>
                            </template>
                            <template v-slot:item.Experience_NameFr="props">
                                <td v-if="isEditing(props.item.Id)" class="row-backgroundColor">
                                    <v-select v-model="editedUser.Experience_Code"
                                              background-color="white"
                                              outlined dense hide-details
                                              :items="experienceList"
                                              :placeholder="$t('division')"
                                              :rules="requiredRules"
                                              :label="$t('division')"
                                              :aria-label="$t('accessibility.selectDivision')"></v-select>
                                </td>
                                <td v-else>
                                    {{displayDivisionName(props.item)}}
                                </td>
                            </template>
                            <template v-slot:item.CreatedDateUtc="props">
                                <td>
                                    {{props.item.CreatedDateUtc ? new Date(props.item.CreatedDateUtc).toLocaleDateString($i18n.locale) : ''}}
                                </td>
                            </template>
                            <template v-slot:item.SoftDelete="props">
                                <td>
                                    <v-icon 
                                        :color="props.item.SoftDelete ? 'error' : 'success'"
                                        :aria-label="props.item.SoftDelete ? $t('accessibility.userInactive') : $t('accessibility.userActive')"
                                        role="img">
                                        {{props.item.SoftDelete ? 'mdi-account-off-outline' : 'mdi-account-check-outline'}}
                                    </v-icon>
                                    <span class="sr-only">
                                        {{props.item.SoftDelete ? $t('accessibility.userInactive') : $t('accessibility.userActive')}}
                                    </span>
                                </td>
                            </template>
                            <template v-for="group in groupedClientIndexAttributes" v-slot:[`item.attr_${group.firstId}`]="props">
                                <td :key="`attr_${group.firstId}`">
                                    <span>{{getAttributeValue(props.item, group.firstId)}}</span>
                                    <v-tooltip v-if="group.attrs.length > 1 && getGroupAttributeValues(props.item, group)" bottom max-width="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <span 
                                                v-bind="attrs" 
                                                v-on="on" 
                                                style="cursor:pointer" 
                                                tabindex="0"
                                                role="button"
                                                :aria-label="$t('accessibility.showMore')"
                                                @click.stop="copyToClipboard(getGroupAttributeValues(props.item, group))"
                                                @keydown.enter="copyToClipboard(getGroupAttributeValues(props.item, group))"
                                                @keydown.space.prevent="copyToClipboard(getGroupAttributeValues(props.item, group))">
                                                &hellip;
                                            </span>
                                        </template>
                                        <span>{{getGroupAttributeValues(props.item, group)}}</span>
                                    </v-tooltip>
                                </td>
                            </template>
                            <template v-slot:item.Actions="props">
                                <td v-if="isEditing(props.item.Id)" class="row-backgroundColor">
                                    <v-hover v-slot="{hover}">
                                        <v-btn class="rounded-0"
                                               height="48px"
                                               :dark="canSave"
                                               depressed tile
                                               :disabled="!canSave || savingNewUser"
                                               :color="hover ? 'buttonHover' : 'primary'"
                                               @click="onSave"
                                               :aria-label="$t('accessibility.saveUser')"
                                               :aria-busy="savingNewUser ? 'true' : 'false'">
                                            <v-col class="pa-0 ma-0">
                                                <v-icon aria-hidden="true">mdi-content-save</v-icon>
                                                <v-progress-linear v-if="savingNewUser" indeterminate aria-hidden="true"></v-progress-linear>
                                                <span v-if="savingNewUser" class="sr-only">
                                                    {{$t('accessibility.savingUser')}}
                                                </span>
                                            </v-col>
                                        </v-btn>
                                    </v-hover>
                                    <v-hover v-slot="{hover}">
                                        <v-btn class="rounded-0"
                                               height="48px"
                                               dark depressed tile
                                               :color="hover ? 'buttonHover' : 'primary'"
                                               @click="cancelEdit"
                                               :aria-label="$t('accessibility.cancelEdit')">
                                            <v-icon aria-hidden="true">mdi-close</v-icon>
                                        </v-btn>
                                    </v-hover>
                                </td>
                                <td v-else align="center">
                                    <v-tooltip bottom open-delay="1000">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-hover v-slot="{hover}">
                                                <v-btn
                                                    icon
                                                    small
                                                    :color="hover ? 'buttonHover' : ''"
                                                    :disabled="props.item.Role_Code != 'USER'"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click="editReportAccess(props.item)"
                                                    :aria-label="$t('user-management.reportAccess')">
                                                    <v-icon class="pr-1" aria-hidden="true">
                                                        mdi-file-lock-outline
                                                    </v-icon>
                                                </v-btn>
                                            </v-hover>
                                        </template>
                                        <span>{{$t('user-management.reportAccess')}}</span>
                                    </v-tooltip>
                                    <v-tooltip bottom open-delay="1000">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-hover v-slot="{hover}">
                                                <v-btn
                                                    icon
                                                    small
                                                    :color="hover ? 'buttonHover' : ''"
                                                    @click="startEditing(props.item)"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :aria-label="$t('accessibility.editUser')">
                                                    <v-icon class="pr-1" aria-hidden="true">
                                                        mdi-pencil-outline
                                                    </v-icon>
                                                </v-btn>
                                            </v-hover>
                                        </template>
                                        <span>{{$t('edit')}}</span>
                                    </v-tooltip>
                                    <v-tooltip bottom open-delay="1000">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-hover v-slot="{hover}">
                                                <v-btn
                                                    icon
                                                    small
                                                    :color="hover ? 'buttonHover' : ''"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click="viewUserDocuments(props.item)"
                                                    :aria-label="$t('user-management.viewDocuments')">
                                                    <v-icon class="pr-1" aria-hidden="true">
                                                        mdi-file-document-multiple-outline
                                                    </v-icon>
                                                </v-btn>
                                            </v-hover>
                                        </template>
                                        <span>{{$t('user-management.viewDocuments')}}</span>
                                    </v-tooltip>
                                    <v-tooltip bottom open-delay="1000">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-hover v-slot="{hover}">
                                                <v-btn
                                                    icon
                                                    small
                                                    @click="resetPassword(props.item)"
                                                    :color="hover ? 'buttonHover' : ''"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :aria-label="$t('user-management.resetPassword')">
                                                    <v-icon class="pr-1" aria-hidden="true">
                                                        mdi-lock-outline
                                                    </v-icon>
                                                </v-btn>
                                            </v-hover>
                                        </template>
                                        <span>{{$t('user-management.resetPassword')}}</span>
                                    </v-tooltip>
                                    <v-tooltip bottom open-delay="1000">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-hover v-slot="{hover}">
                                                <v-btn
                                                    icon
                                                    small
                                                    @click="deleteUser(props.item)"
                                                    :color="hover ? 'buttonHover' : ''"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :aria-label="$t('accessibility.deleteUser')">
                                                    <v-icon class="pr-1" aria-hidden="true">
                                                        mdi-close-circle-outline
                                                    </v-icon>
                                                </v-btn>
                                            </v-hover>
                                        </template>
                                        <span>{{$t('delete')}}</span>
                                    </v-tooltip>
                                </td>
                            </template>
                            <template #footer.page-text="props">
                                <span aria-live="polite" aria-atomic="true">
                                    {{props.pageStart}}-{{props.pageStop}} {{$t('of').toLowerCase()}} {{props.itemsLength}}
                                </span>
                            </template>
                        </v-data-table>
                    </v-form>
                </v-card>

                <confirmation-dialog v-model="editConfirmationDialog"
                                     type="edit"
                                     :title="$t('user-management.confirmEditTitle')"
                                     :subtitle="$t('user-management.confirmEditSubtitle', { fullName: this.editedUser.FirstName + ' ' + this.editedUser.LastName})"
                                     :btnOKText="$t('user-management.confirm')"
                                     :btnCancelText="$t('user-management.cancel')"
                                     @commit="commitEditing"
                                     @cancel="editConfirmationDialog = false" />
                <confirmation-dialog v-if="confirmableItem != null"
                                     v-model="deleteConfirmationDialog"
                                     type="delete"
                                     :title="$t('user-management.deleteTitle', { fullName: this.confirmableItem.FirstName + ' ' + this.confirmableItem.LastName})"
                                     :subtitle="$t('user-management.deleteText')"
                                     :btnOKText="$t('user-management.yesDeleteIt')"
                                     @commit="commitDelete"
                                     @cancel="deleteConfirmationDialog = false" />
                <confirmation-dialog v-if="confirmableItem != null"
                                     type="resetPassword"
                                     v-model="passwordConfirmationDialog"
                                     :title="$t('user-management.resetTitle')"
                                     :subtitle="$t('user-management.resetText', { 'firstName': confirmableItem.FirstName, 'lastName': confirmableItem.LastName })"
                                     btnOKText="OK"
                                     @commit="commitReset"
                                     @cancel="passwordConfirmationDialog = false" />
                <confirmation-dialog v-if="editedUser != null"
                                     type="editLanguage"
                                     v-model="languageSelectorDialog"
                                     :title="$t('user-management.languageTitle')"
                                     :subtitle="$t('user-management.languageText', { 'firstName': editedUser.FirstName, 'lastName': editedUser.LastName })"
                                     btnOKText="OK"
                                     @commit="commitNew"
                                     @cancel="languageSelectorDialog = false"
                                     @toggle="toggleUserLanguage"
                                     :toggleItems="{'text1': 'EN', 'text2': 'FR', 'value1': 'en', 'value2': 'fr', 'model': editedUser.Language}"
                                     :persistent="true" />

                <v-dialog 
                    v-model="successSnackbar" 
                    max-width="615px"
                    role="dialog"
                    :aria-label="$t('accessibility.success')">
                    <v-card class="pa-8 rounded-xl" flat>
                        <v-row align="center" justify="center">
                            <v-icon size="96px"
                                    color="#74DF13"
                                    class="my-5"
                                    :aria-label="$t('accessibility.successIcon')"
                                    role="img">mdi-check-circle-outline</v-icon>
                        </v-row>
                        <v-card-subtitle 
                            class="text-center card-subtitle font-weight-medium mt-1">
                            <span v-html="successMessage"></span>
                        </v-card-subtitle>
                    </v-card>
                </v-dialog>
                <v-dialog 
                    v-model="errorSnackbar" 
                    max-width="615px"
                    role="alertdialog"
                    :aria-label="$t('accessibility.error')">
                    <v-card class="pa-8 rounded-xl" flat>
                        <v-row align="center" justify="center">
                            <v-icon size="96px"
                                    color="red"
                                    class="my-5"
                                    :aria-label="$t('accessibility.errorIcon')"
                                    role="img">mdi-close-circle-outline</v-icon>
                        </v-row>
                        <v-card-subtitle 
                            class="text-center card-subtitle font-weight-medium mt-1"
                            role="alert">
                            {{ $t('user-management.cantEditText') }}
                        </v-card-subtitle>
                        <v-card-subtitle 
                            class="text-center card-subtitle font-weight-medium mt-1"
                            role="alert">
                            {{ (errorMessage != null? $t(errorMessage): '') }}
                        </v-card-subtitle>
                        <v-card-actions>
                            <v-row justify="center">
                                <v-btn 
                                    class="text-normal" 
                                    depressed 
                                    color="primary" 
                                    width="33%" 
                                    x-large 
                                    @click="errorSnackbar = false"
                                    :aria-label="$t('ok')">OK</v-btn>
                            </v-row>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
                <v-dialog 
                    v-model="columnSelectorDialog" 
                    max-width="400px"
                    role="dialog"
                    :aria-label="$t('user-management.customerColumnsTitle')">
                    <v-card class="pa-6 rounded-xl" flat>
                        <v-card-title class="px-0 pt-0 font-weight-bold" role="heading" aria-level="2">
                            {{$t('user-management.customerColumnsTitle')}}
                        </v-card-title>
                        <v-list dense role="group" :aria-label="$t('accessibility.selectColumnsToDisplay')">
                            <v-list-item v-for="col in selectableColumns" :key="col.value" class="px-0">
                                <v-checkbox v-model="tempVisibleColumns"
                                            :value="col.value"
                                            :label="col.text"
                                            hide-details
                                            dense
                                            class="mt-0"
                                            :aria-label="col.text"></v-checkbox>
                            </v-list-item>
                        </v-list>
                        <v-card-actions class="px-0 pt-4">
                            <v-hover v-slot="{hover}">
                                <v-btn :color="hover ? 'buttonHover' : 'primary'"
                                       depressed dark
                                       class="text-none mr-2"
                                       @click="applyColumnSelection">
                                    {{$t('user-management.apply')}}
                                </v-btn>
                            </v-hover>
                            <v-hover v-slot="{hover}">
                                <v-btn :color="hover ? 'buttonHover' : ''"
                                       depressed
                                       class="text-none"
                                       @click="columnSelectorDialog = false">
                                    {{$t('user-management.cancel')}}
                                </v-btn>
                            </v-hover>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

            </v-col>
        </v-row>
    </v-container>
</template>
<script>
    import { mapState } from "vuex";
    import confirmationDialog from '../../components/confirmationDialog'
    import reportAccessComponent from '../../components/reportAccess'
    import {
        emailRules,
        usernameRules,
        firstNameRules,
        lastNameRules,
        requiredFieldRules
    } from '../../utils/validationRules';

    export default {
        components: { reportAccessComponent,confirmationDialog },
        name: "UserManagement",
        data() {
            return {
                search: "",
                users: [],
                roles: [],
                experiences: [],
                clients: [],
                clientIndexAttributes: [],
                tableOptions: {
                    itemsPerPage: 25,
                    page: 1,
                    sortBy: ['Id'],
                    sortDesc: [true],
                },
                loading: true,
                initial: true,
                totalUsers: 0,
                backupUser: "",
                editedUser: {},
                editedUserIndex: -1,
                editConfirmationDialog: false,
                dialogReportAccess: false,
                errorSnackbar: false,
                errorMessage: undefined,
                successSnackbar: false,
                successMessage: undefined,
                confirmableItem: undefined,
                deleteConfirmationDialog: false,
                passwordConfirmationDialog: false,
                languageSelectorDialog: false,
                columnSelectorDialog: false,
                visibleColumns: JSON.parse(localStorage.getItem('userManagement_visibleColumns')) || ['FirstName', 'LastName', 'Email'],
                tempVisibleColumns: [],
                roleFilters: [],
                formValid: false,
                // Enhanced validation rules for WCAG AA (3.3.3)
                requiredRules: requiredFieldRules(this.$t.bind(this)),
                emailRules: emailRules(this.$t.bind(this)),
                usernameRules: usernameRules(this.$t.bind(this)),
                firstnameRules: firstNameRules(this.$t.bind(this)),
                lastnameRules: lastNameRules(this.$t.bind(this)),
                savingNewUser: false,
                accessibilityObserver: null
            }
        },
        mounted() {
            this.loadDataLists();
            this.loadUsers();

            // Fix nested interactive controls accessibility issue in v-data-table footer
            this.$nextTick(() => {
                this.fixDataTableFooterAccessibility();
                this.setupAccessibilityObserver();
            });
        },
        beforeDestroy() {
            // Clean up the observer when component is destroyed
            if (this.accessibilityObserver) {
                this.accessibilityObserver.disconnect();
            }
        },
        computed: {
            ...mapState("account", ["user"]),
            roleFilterList() {
                return [
                    this.$t('user-management.user'),
                    this.$t('user-management.admin'),
                    this.$t('user-management.superUser'),
                    this.$t('user-management.customerService'),
                    this.$t('user-management.sogicaInternal')
                ]
            },
            listHeaders() {
                let allHeaders = [
                    { text: this.formatMenuText(this.$t("id")), value: "Id", class: ["text-normal", "font-weight-medium"], width:"5%" },
                    { text: this.formatMenuText(this.$t("role")), value: "Role_NameFr", class: ["text-normal", "font-weight-medium"], width: "10%" },
                    { text: this.formatMenuText(this.$t('firstname')), value: "FirstName", class: ["text-normal", "font-weight-medium"], width: "10%", selectable: true },
                    { text: this.formatMenuText(this.$t("lastname")), value: "LastName", class: ["text-normal", "font-weight-medium"], width: "10%", selectable: true },
                    { text: this.formatMenuText(this.$t("username")), value: "Username", class: ["text-normal", "font-weight-medium"], width: "10%" },
                    { text: this.formatMenuText(this.$t("email")), value: "Email", class: ["text-normal", "font-weight-medium"], width: "10%", selectable: true },
                    { text: this.formatMenuText(this.$t("client")), value: "Client_NameFR", class: ["text-normal", "font-weight-medium"], width: "10%" },
                    { text: this.formatMenuText(this.$t("division")), value: "Experience_NameFr", class: ["text-normal", "font-weight-medium"], width: "10%"},
                    { text: this.formatMenuText(this.$t('user-management.columnRegistrationDate')), value: "CreatedDateUtc", class: ["text-normal", "font-weight-medium"], width: "10%", selectable: true },
                    { text: this.formatMenuText(this.$t('user-management.columnStatus')), value: "SoftDelete", class: ["text-normal", "font-weight-medium"], width: "8%", selectable: true, sortable: false },
                    ...this.groupedClientIndexAttributes.map(group => ({
                        text: this.formatMenuText(this.$i18n.locale === 'fr' ? group.displayNameFR : group.displayNameEN),
                        value: `attr_${group.firstId}`,
                        class: ["text-normal", "font-weight-medium"],
                        width: "10%",
                        sortable: false,
                        selectable: true
                    })),
                    { text: '', value: "Actions", class: ["text-normal", "font-weight-medium"], width:"10%", sortable: false }
                ];
                return allHeaders.filter(h => !h.selectable || this.visibleColumns.includes(h.value));
            },
            groupedClientIndexAttributes() {
                const groups = [];
                const seen = {};
                for (const attr of this.clientIndexAttributes) {
                    const key = attr.DisplayNameEN;
                    if (!seen[key]) {
                        seen[key] = { displayNameEN: attr.DisplayNameEN, displayNameFR: attr.DisplayNameFR, firstId: attr.Id, attrs: [] };
                        groups.push(seen[key]);
                    }
                    seen[key].attrs.push(attr);
                }
                return groups;
            },
            selectableColumns() {
                const fixed = [
                    { value: 'FirstName', text: this.formatMenuText(this.$t('user-management.columnFirstName')) },
                    { value: 'LastName', text: this.formatMenuText(this.$t('user-management.columnLastName')) },
                    { value: 'Email', text: this.formatMenuText(this.$t('user-management.columnEmail')) },
                    { value: 'CreatedDateUtc', text: this.formatMenuText(this.$t('user-management.columnRegistrationDate')) },
                    { value: 'SoftDelete', text: this.formatMenuText(this.$t('user-management.columnStatus')) },
                ];
                const dynamic = this.groupedClientIndexAttributes.map(group => ({
                    value: `attr_${group.firstId}`,
                    text: this.formatMenuText(this.$i18n.locale === 'fr' ? group.displayNameFR : group.displayNameEN)
                }));
                return [...fixed, ...dynamic];
            },
            fullname: function () {
                return this.user.lastname + ', ' + this.user.firstname;
            },
            editedUserClientCode() {
                return this.editedUser.Client_Code;
            },
            clientList() {
                let list = [];

                for (let i = 0; i < this.clients.length; i++) {
                    list.push({ value: this.clients[i].Code, text: this.$i18n.locale == "fr" ? this.clients[i].NameFR : this.clients[i].NameEN });
                }
                return list;
            },
            experienceList() {
                let list = [];

                if (this.editedUser.Client_Code != null) {
                    for (let i = 0; i < this.experiences.length; i++) {
                        if (this.experiences[i].ClientCode !== this.editedUser.Client_Code) continue;
                        list.push({ value: this.experiences[i].Code, text: this.$i18n.locale == "fr" ? this.experiences[i].NameFr : this.experiences[i].NameEn });
                    }
                }
                return list;
            },
            roleList() {
                let list = [];

                for (let i = 0; i < this.roles.length; i++) {
                    list.push({ value: this.roles[i].Code, text: this.displayRoleName(this.roles[i], this.user.client), Code: this.roles[i].Code });
                }
                return list;
            },
            canSave() {
                return (this.editedUserIndex != -1)
                    && (this.formValid)
                    && (this.editedUser.Role_Code != this.users[this.editedUserIndex].Role_Code
                    || this.editedUser.FirstName != this.users[this.editedUserIndex].FirstName
                    || this.editedUser.LastName != this.users[this.editedUserIndex].LastName
                    || this.editedUser.Username != this.users[this.editedUserIndex].Username
                    || this.editedUser.Email != this.users[this.editedUserIndex].Email
                    || this.editedUser.Client_Code != this.users[this.editedUserIndex].Client_Code
                    || this.editedUser.Experience_Code != this.users[this.editedUserIndex].Experience_Code
                )
            },
        },
        watch: {
            tableOptions: {
                deep: true,
                handler() {
                    if (!this.loading)
                        this.loadUsers(false);
                    // Reapply accessibility fix when table updates
                    this.$nextTick(() => {
                        this.fixDataTableFooterAccessibility();
                    });
                }
            },
            roleFilters: {
                deep: true,
                handler() {
                    this.loadUsers();
                }
            },
            successSnackbar: function (newVal) {
                if(newVal == true) {
                    setTimeout(() => {
                        this.successSnackbar = false;
                        this.successMessage = undefined;
                    }, 2000);
                }
            },
            editedUserClientCode: function (newVal, oldVal) {
                if ((newVal) && (oldVal) && (newVal != oldVal)) {
                    this.editedUser.Experience_Code = '';
                }
                if (this.experienceList.length === 1)
                    this.editedUser.Experience_Code = this.experienceList[0].value;
            }
        },
        methods: {
            formatMenuText(text) {
                if (!text) return text;

                // Check the titleCase setting from the current experience
                const titleCase = this.$experience.titleCase !== false; // default to true if not set

                if (titleCase) {
                    // Title case: capitalize first letter of all words
                    return text.split(' ').map(word => {
                        if (word.length === 0) return word;
                        // Check if word is an acronym (all uppercase)
                        if (word === word.toUpperCase() && word.length > 1) {
                            return word; // Keep acronyms as-is
                        }
                        return word.charAt(0).toUpperCase() + word.slice(1);
                    }).join(' ');
                } else {
                    // Sentence case: only capitalize first letter of first word
                    // But preserve acronyms
                    const words = text.split(' ');
                    return words.map((word, index) => {
                        if (word.length === 0) return word;
                        // Check if word is an acronym (all uppercase)
                        if (word === word.toUpperCase() && word.length > 1) {
                            return word; // Keep acronyms as-is
                        }
                        if (index === 0) {
                            // First word: capitalize first letter
                            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                        } else {
                            // Other words: all lowercase
                            return word.toLowerCase();
                        }
                    }).join(' ');
                }
            },
            fixDataTableFooterAccessibility() {
                const footerSelectors = this.$el.querySelectorAll('.v-data-footer .v-input__slot[role="button"]');
                footerSelectors.forEach(el => {
                    el.removeAttribute('role');
                    // Also remove aria-haspopup since it's no longer a button
                    if (el.getAttribute('aria-haspopup') === 'listbox') {
                        el.removeAttribute('aria-haspopup');
                    }
                });

                const ariaOwnsDivs = this.$el.querySelectorAll('div[aria-owns]');
                ariaOwnsDivs.forEach(el => {
                    const ariaOwnsValue = el.getAttribute('aria-owns');
                    // Check if the referenced element exists
                    if (ariaOwnsValue && !document.getElementById(ariaOwnsValue)) {
                        // If the element doesn't exist, remove the aria-owns attribute
                        el.removeAttribute('aria-owns');
                    }
                    // Also remove aria-expanded from divs with aria-owns (not supported combination)
                    if (el.hasAttribute('aria-expanded')) {
                        el.removeAttribute('aria-expanded');
                    }
                });

                // Fix 3: Remove aria-expanded from .v-input__slot divs (not supported on div without proper role)
                const inputSlots = this.$el.querySelectorAll('.v-data-footer .v-input__slot[aria-expanded]');
                inputSlots.forEach(el => {
                    el.removeAttribute('aria-expanded');
                });

                // Fix 4: Add proper ARIA attributes to the actual table element
                const tableElement = this.$el.querySelector('.v-data-table table');
                if (tableElement) {
                    tableElement.setAttribute('aria-rowcount', this.totalUsers);
                    tableElement.setAttribute('aria-busy', this.loading ? 'true' : 'false');
                }
            },
            setupAccessibilityObserver() {
                // Create a MutationObserver to watch for Vuetify re-adding problematic attributes
                this.accessibilityObserver = new MutationObserver(() => {
                    this.fixDataTableFooterAccessibility();
                });

                // Observe the footer for attribute changes
                const footer = this.$el.querySelector('.v-data-footer');
                if (footer) {
                    this.accessibilityObserver.observe(footer, {
                        attributes: true,
                        attributeFilter: ['aria-expanded', 'role', 'aria-haspopup', 'aria-owns'],
                        subtree: true
                    });
                }
            },
            isEditing(userID) {
                return !this.dialogReportAccess && (this.editedUser.Id == userID);
            },
            displayUserRoleName(item) {
                const role = this.roles.find(r => r.Code == item.Role_Code);
                if (role) {
                    return this.displayRoleName(role, item.Client_Code);
                } else {
                    return '';
                }
            },
            displayRoleName(role, clientCode) {
                if (role) {
                    const roleJson = JSON.parse(this.$i18n.locale == "fr" ? role.NameFr : role.NameEn);
                    return roleJson[clientCode] ? roleJson[clientCode] : roleJson["default"];
                } else {
                    return '';
                }
            },
            displayClientName(item) {
                const client = this.clients.find(c => c.Code == item.Client_Code);
                if (client) {
                    return this.$i18n.locale == "fr" ? client.NameFR : client.NameEN;
                } else {
                    return '';
                }
            },
            displayDivisionName(item) {
                const experience = this.experiences.find(e => e.Code == item.Experience_Code);
                if (experience) {
                    return this.$i18n.locale == "fr" ? experience.NameFr : experience.NameEn;
                } else {
                    return '';
                }
            },
            allowedExperiences(editing) {
                if (editing == null) return this.experiences;
                return this.experiences.filter(x => x.ClientCode === editing.Client_Code);
            },
            startEditing(item) {
                this.cancelEdit();
                this.editedUser = JSON.parse(JSON.stringify(item));
                this.editedUserIndex = this.users.indexOf(item);
            },
            cancelEdit() {
                if (this.editedUser.Id == 0) {
                    this.users.splice(0, 1);
                    this.totalUsers = this.totalUsers > 0 ? this.totalUsers - 1 : 0;
                }
                this.editedUser = {};
                this.editedUserIndex = -1;
            },
            closeEdit() {
                this.editedUser = {};
                this.editedUserIndex = -1;
            },
            createUser() {
                let newUser = { Id: 0, Role_Code: 'USER', FirstName: '', LastName: '', Email: '', Username: '', Client_Code: '', Experience_Code: '', ActiveMFA: true, Language: this.$i18n.locale };
                this.users.unshift(newUser);
                this.totalUsers += 1;
                this.editedUser = JSON.parse(JSON.stringify(newUser));
                this.editedUserIndex = this.users.indexOf(newUser);
                this.savingNewUser = false;

                if (this.clientList.length === 1)
                    this.editedUser.Client_Code = this.clientList[0].value;
                if (this.experienceList.length === 1)
                    this.editedUser.Experience_Code = this.experienceList[0].value;
            },
            onSave() {
                if (this.formValid) {
                    if (this.editedUser.Id == 0) {
                        this.confirmableItem = this.editedUser;
                        this.languageSelectorDialog = true;
                    } else {
                        this.editConfirmationDialog = true;
                    }
                }
            },
            commitEditing() {
                //if (this.formValid) {
                    this.editConfirmationDialog = false;
                    let url = "api/usermanagement/updateuser";
                    let sendData = {
                        userId: this.editedUser.Id,
                        username: this.editedUser.Username,
                        firstName: this.editedUser.FirstName,
                        lastName: this.editedUser.LastName,
                        email: this.editedUser.Email,
                        roleCode: this.editedUser.Role_Code,
                        clientCode: this.editedUser.Client_Code,
                        experienceCode: this.editedUser.Experience_Code,
                        activeMFA: this.editedUser.ActiveMFA
                    };

                    this.$axios.post(url, sendData)
                        .then((response) => {
                            if (response.data.success === true) {
                                this.$set(this.users, this.editedUserIndex, JSON.parse(JSON.stringify(this.editedUser)));
                                this.successMessage = this.$t('user-management.changesSaved');
                                console.log('Success message set to:', this.successMessage);
                                this.$nextTick(() => {
                                    console.log('NextTick callback, setting successSnackbar to true');
                                    this.successSnackbar = true;
                                });
                            } else if (response.data.success == false && response.data.error == 'session-expired') {
                                this.$router.push({ name: 'login' });
                            } 
                            else {
                                this.errorMessage = response.data.msg;
                                this.errorSnackbar = true;
                            }

                        })
                        .then(() => {
                            this.closeEdit();
                        })
                        .catch(() => {
                            this.errorMessage = undefined;
                            this.errorSnackbar = true;
                        });
                //}
            },
            commitNew() {
                let url = "api/usermanagement/createuser";
                this.languageSelectorDialog = false;
                this.savingNewUser = true;
                let sendData = {
                    username: this.editedUser.Username,
                    firstName: this.editedUser.FirstName,
                    lastName: this.editedUser.LastName,
                    email: this.editedUser.Email,
                    roleCode: this.editedUser.Role_Code,
                    clientCode: this.editedUser.Client_Code,
                    experienceCode: this.editedUser.Experience_Code,
                    activeMFA: this.editedUser.ActiveMFA,
                    contactLanguage: this.editedUser.Language
                };

                this.$axios.post(url, sendData)
                    .then((response) => {
                        this.savingNewUser = false;
                        if (response.data.success === true) {
                            this.successMessage = this.$t('user-management.userCreated');
                            this.$nextTick(() => {
                                this.successSnackbar = true;
                            });
                            this.closeEdit();
                            this.loadUsers();
                        } else if (response.data.success == false && response.data.error == 'session-expired') {
                            this.$router.push({ name: 'login' });
                        } 
                        else {
                            this.errorMessage = response.data.msg;
                            this.errorSnackbar = true;
                        }
                    })
                    .catch(() => {
                        this.errorMessage = undefined;
                        this.errorSnackbar = true;
                        this.savingNewUser = false;
                    });
                //}
            },
            commitReset() {
                this.passwordConfirmationDialog = false;
                let sendData = {
                    userId: this.confirmableItem.Id
                };

                this.$axios.post('api/usermanagement/resetpassword', sendData)
                    .then((response) => {
                        if (response.data.success === true) {
                            this.successMessage = this.$t('user-management.emailSent', { fullName: this.confirmableItem.FirstName + ' ' + this.confirmableItem.LastName });
                            this.$nextTick(() => {
                                this.successSnackbar = true;
                            });
                        } else if (response.data.success == false && response.data.error == 'session-expired') {
                            this.$router.push({ name: 'login' });
                        } 
                        else {
                            this.errorMessage = response.data.msg;
                            this.errorSnackbar = true;
                        }
                    })
                    .catch(() => {
                        this.errorMessage = undefined;
                        this.errorSnackbar = true;
                    });
            },
            commitDelete() {
                this.deleteConfirmationDialog = false;

                let sendData = {
                    userId: this.confirmableItem.Id
                };

                this.$axios.post('api/usermanagement/softdelete', sendData)
                    .then((response) => {
                        if (response.data.success === true) {
                            let userIndex = this.users.findIndex(x => x.Id === this.confirmableItem.Id);
                            if (userIndex >= 0) {
                                this.users.splice(userIndex, 1);
                            }
                            this.successMessage = this.$t('user-management.userDeleted', { fullName: this.confirmableItem.FirstName + ' ' + this.confirmableItem.LastName });
                            this.$nextTick(() => {
                                this.successSnackbar = true;
                            });
                        } else if (response.data.success == false && response.data.error == 'session-expired') {
                            this.$router.push({ name: 'login' });
                        } 
                        else {
                            this.errorMessage = response.data.msg;
                            this.errorSnackbar = true;
                        }
                    })
                    .catch(() => {
                        this.errorMessage = undefined;
                        this.errorSnackbar = true;
                    });
            },
            buildRoleFiltersRequest() {
                const roleIdlist = [];
                for (let i = 0; i < this.roleFilters.length; i++) {
                    roleIdlist.push(this.roleList[this.roleFilters[i]].Code );
                }
                return roleIdlist;
            },
            buildRequest(newQuery) {
                if (newQuery)
                    this.tableOptions.page = 1;

                let request = {
                    search: this.search,
                    options: this.tableOptions,
                    roleFilters: this.buildRoleFiltersRequest()
                }
                return request;
            },
            loadUsers(newQuery = true) {
                const self = this;
                this.initial = false;
                this.loading = true;
                const request = this.buildRequest(newQuery);

                return this.$axios.post("api/usermanagement/users", request)
                    .then((response) => {
                        if (response.data.success == false && response.data.error == 'session-expired') {
                            self.$router.push({ name: 'login' });
                        } 
                        self.users = response.data.Users;
                        self.totalUsers = response.data.TotalUsers;
                    })
                    .then(() => {
                        self.loading = false;
                        // Fix accessibility issue after data loads
                        self.$nextTick(() => {
                            self.fixDataTableFooterAccessibility();
                        });
                    });

            },
            loadDataLists() {
                const self = this;
                this.initial = false;
                this.loading = true;

                return this.$axios.get("api/usermanagement/getdatalists")
                    .then((response) => {
                        if (response.data.success == false && response.data.error == 'session-expired') {
                            self.$router.push({ name: 'login' });
                        } 
                        self.roles = response.data.Roles;
                        self.experiences = response.data.Experiences;
                        self.clients = response.data.Clients;
                        self.clientIndexAttributes = response.data.ClientIndexAttributes ?? [];
                    })
                    .then(() => {
                        self.loading = false;
                    });
            },
            deleteUser(user) {
                this.confirmableItem = user;
                this.deleteConfirmationDialog = true;
            },
            resetPassword(user) {
                this.confirmableItem = user;
                this.passwordConfirmationDialog = true;

            },
            editReportAccess(user) {
                this.editedUserIndex = this.users.indexOf(user);
                this.backupUser = JSON.stringify(user);
                this.editedUser = Object.assign({}, user);
                this.dialogReportAccess = true;
            },
            onReportAccessCancel() {
                Object.assign(this.users[this.editedUserIndex], JSON.parse(this.backupUser))
                this.editedUserIndex = -1;
                this.editedUser = {};
                this.dialogReportAccess = false;
            },
            onReportAccessSave(user) {
                Object.assign(this.users[this.editedUserIndex], user)
                this.editedUserIndex = -1;
                this.editedUser = {};
                this.dialogReportAccess = false;
            },
            viewUserDocuments(user) {
                this.$router.push({ name: 'home', query: { userId: user.Id } });
            },
            onClearSearch() {
                let self = this;
                this.search = '';
                this.$nextTick(() => {
                    self.$refs['searchField'].blur()
                });
            },
            textLengthRule(fieldName, n) {
                return this.$t('textLengthRule', { field: this.$t(fieldName), n: n })
            },
            toggleUserLanguage() {
                if (this.editedUser.Language == 'en')
                    this.editedUser.Language = 'fr'
                else
                    this.editedUser.Language = 'en'
            },
            openColumnSelector() {
                this.tempVisibleColumns = [...this.visibleColumns];
                this.columnSelectorDialog = true;
            },
            applyColumnSelection() {
                this.visibleColumns = [...this.tempVisibleColumns];
                localStorage.setItem('userManagement_visibleColumns', JSON.stringify(this.visibleColumns));
                this.columnSelectorDialog = false;
            },
            getGroupAttributeValues(user, group) {
                return group.attrs.map(attr => this.getAttributeValue(user, attr.Id)).filter(v => v).join(', ');
            },
            copyToClipboard(text) {
                navigator.clipboard.writeText(text);
            },
            getAttributeValue(user, attributeId) {
                if (!user.Reports) return '';
                const values = [];
                for (const report of user.Reports) {
                    if (!report.UserConfiguredCriteria) continue;
                    for (const group of report.UserConfiguredCriteria) {
                        for (const crit of group) {
                            if (crit.ReportAttributeId === attributeId && crit.Value) {
                                values.push(crit.Value);
                            }
                        }
                    }
                }
                return values.join(', ');
            }
        }
    }
</script>
<style scoped>
    .v-btn-toggle .v-btn {
        margin: 4px !important;
        border-radius: 10px !important;
    }
    .input-height-normal {
        height: 47px !important;
    }
    .row-backgroundColor {
        background-color: var(--rowHighlightColor) !important;
    }

</style>